import React from 'react';
import { X, Building2, Mail, Phone, User, Loader2, PlusCircle } from 'lucide-react';

/**
 * Confirmation modal before creating an organisation from an account creation request.
 *
 * Props:
 *  - request   : the AccountCreationRequest object
 *  - onConfirm : async callback — called when the admin clicks "Confirmer"
 *  - onClose   : callback to close the modal
 *  - loading   : boolean — shows spinner while the API call is in progress
 */
export default function CreateOrganisationModal({ request, onConfirm, onClose, loading }) {
  if (!request) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-base font-semibold text-gray-900">Confirmer la création</h3>
          <button
            onClick={onClose}
            disabled={loading}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          <p className="text-sm text-gray-600">
            Vous êtes sur le point de créer une organisation à partir de cette demande.
            Un compte administrateur sera créé et un email de bienvenue sera envoyé.
          </p>

          <div className="bg-gray-50 rounded-lg p-4 space-y-3 text-sm">
            <div className="flex items-center gap-3">
              <Building2 className="h-4 w-4 text-gray-400 flex-shrink-0" />
              <div>
                <span className="text-gray-500">Organisation</span>
                <p className="font-medium text-gray-900">{request.organisation_name}</p>
              </div>
            </div>

            {request.organisation_description && (
              <div className="flex items-start gap-3">
                <Building2 className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
                <div>
                  <span className="text-gray-500">Description</span>
                  <p className="font-medium text-gray-900">{request.organisation_description}</p>
                </div>
              </div>
            )}

            <div className="flex items-center gap-3">
              <Mail className="h-4 w-4 text-gray-400 flex-shrink-0" />
              <div>
                <span className="text-gray-500">Email admin</span>
                <p className="font-medium text-gray-900">{request.email}</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Phone className="h-4 w-4 text-gray-400 flex-shrink-0" />
              <div>
                <span className="text-gray-500">Contact</span>
                <p className="font-medium text-gray-900">{request.contact_number}</p>
              </div>
            </div>

            {(request.first_name || request.last_name) && (
              <div className="flex items-center gap-3">
                <User className="h-4 w-4 text-gray-400 flex-shrink-0" />
                <div>
                  <span className="text-gray-500">Nom du responsable</span>
                  <p className="font-medium text-gray-900">
                    {[request.first_name, request.last_name].filter(Boolean).join(' ')}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Annuler
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Création en cours...
              </>
            ) : (
              <>
                <PlusCircle className="h-4 w-4 mr-2" />
                Confirmer la création
              </>
            )}
          </button>
        </div>

      </div>
    </div>
  );
}
